/**
 * Web Speech API Wrapper.
 * Bevorzugt deutsche Stimme (Pflicht für SHK-Lerninhalte).
 */
const DE_LANG_PREFIXES = ['de-de', 'de-at', 'de-ch', 'de'];

export interface TtsOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
}

export class Tts {
  private synth: SpeechSynthesis | null = null;
  private cachedVoice: SpeechSynthesisVoice | null = null;

  constructor(
    synth: SpeechSynthesis | null = (globalThis as unknown as { speechSynthesis?: SpeechSynthesis })
      .speechSynthesis ?? null,
  ) {
    this.synth = synth;
    // Android Chrome lädt getVoices() asynchron und feuert 'voiceschanged',
    // sobald die Liste verfügbar ist. Cache invalidieren, damit beim nächsten
    // pick die (nun geladene / bessere) Stimme gewählt wird.
    const target = this.synth as unknown as {
      addEventListener?: (t: string, cb: () => void) => void;
    } | null;
    if (target && typeof target.addEventListener === 'function') {
      target.addEventListener('voiceschanged', () => {
        this.cachedVoice = null;
      });
    }
  }

  isAvailable(): boolean {
    return this.synth !== null;
  }

  pickGermanVoice(): SpeechSynthesisVoice | null {
    if (!this.synth) return null;
    if (this.cachedVoice) return this.cachedVoice;
    const voices = this.synth.getVoices();
    for (const prefix of DE_LANG_PREFIXES) {
      const matches = voices.filter((vv) => vv.lang.toLowerCase().startsWith(prefix));
      if (matches.length) {
        // OS-native Stimme bevorzugen: "Google …"-Stimmen sind Cloud-TTS und
        // brauchen Internet — auf einer Offline-PWA ungeeignet.
        const best =
          matches.find((vv) => vv.localService && !/google/i.test(vv.name)) ??
          matches.find((vv) => !/google/i.test(vv.name)) ??
          matches[0];
        if (best) {
          this.cachedVoice = best;
          return best;
        }
      }
    }
    return null;
  }

  speak(text: string, opts: TtsOptions = {}): void {
    if (!this.synth) return;
    this.synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = this.pickGermanVoice();
    if (voice) u.voice = voice;
    u.lang = voice?.lang ?? 'de-DE';
    u.rate = opts.rate ?? 1.0;
    u.pitch = opts.pitch ?? 1.0;
    u.volume = opts.volume ?? 1.0;
    this.synth.speak(u);
  }

  cancel(): void {
    this.synth?.cancel();
  }
}
